// Dated, sourced policy registry. Every policy parameter the app uses lives
// here with the date it took effect and the official page it came from. The
// `snapshot` is a Firecrawl copy of that page committed under policy/sources/
// (refresh with the "Fetch eligibility data" workflow, set=policy).
//
// Lookups take a date, so a transaction on either side of a policy change gets
// the rule that applied to it. Scope: Singapore Citizens and PRs only.
//
// Policy changes: add a new version with its effectiveFrom date. Never edit an
// old version's numbers; those are history.

export type Residency = "SC" | "PR";

export interface Source {
  url: string;
  snapshot: string; // path under policy/sources/
  fetched: string; // ISO date the snapshot was taken
}

export interface Versioned<T> {
  effectiveFrom: string; // ISO date, inclusive
  value: T;
  source: Source;
  note?: string;
  // Fields (or "effectiveFrom") the cited snapshot does NOT state. Shown as
  // UNVERIFIED in the assumptions register until a source is found.
  unverified?: string[];
}

const IRAS_BSD: Source = {
  url: "https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/buyer's-stamp-duty-(bsd)",
  snapshot: "policy/sources/www-iras-gov-sg-taxes-stamp-duty-for-property-buying-or-acquiring-property-buyer-s-stamp-duty-bsd.md",
  fetched: "2026-10-05",
};
const IRAS_ABSD: Source = {
  url: "https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/additional-buyer's-stamp-duty-(absd)",
  snapshot: "policy/sources/www-iras-gov-sg-taxes-stamp-duty-for-property-buying-or-acquiring-property-additional-buyer-s-stamp-duty-absd.md",
  fetched: "2026-10-05",
};
const IRAS_SSD: Source = {
  url: "https://www.iras.gov.sg/taxes/stamp-duty/for-property/selling-or-disposing-property/seller's-stamp-duty-(ssd)-for-residential-property",
  snapshot: "policy/sources/www-iras-gov-sg-taxes-stamp-duty-for-property-selling-or-disposing-property-seller-s-stamp-duty-ssd-for-residential-prop.md",
  fetched: "2026-10-05",
};
const MAS_2022: Source = {
  url: "https://www.mas.gov.sg/news/media-releases/2022/measures-to-promote-sustainable-conditions-in-the-property-market-by-ensuring-prudent-borrowing-and-moderating-demand",
  snapshot: "policy/sources/www-mas-gov-sg-news-media-releases-2022-measures-to-promote-sustainable-conditions-in-the-property-market-by-ensuring-pr.md",
  fetched: "2026-10-05",
};
const MAS_MSR_TDSR: Source = {
  url: "https://www.mas.gov.sg/regulation/explainers/new-housing-loans/msr-and-tdsr-rules",
  snapshot: "policy/sources/www-mas-gov-sg-regulation-explainers-new-housing-loans-msr-and-tdsr-rules.md",
  fetched: "2026-10-05",
};
const MND_2024: Source = {
  url: "https://www.mnd.gov.sg/newsroom/speeches/view/measures-to-cool-the-hdb-resale-market-and-provide-more-support-for-first-time-home-buyers",
  snapshot: "policy/sources/www-mnd-gov-sg-newsroom-speeches-view-measures-to-cool-the-hdb-resale-market-and-provide-more-support-for-first-time-hom.md",
  fetched: "2026-10-05",
};
const MAS_LTV: Source = {
  url: "https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits",
  snapshot: "policy/sources/www-mas-gov-sg-regulation-explainers-new-housing-loans-loan-tenure-and-loan-to-value-limits.md",
  fetched: "2026-10-05",
};
const CPF_USAGE: Source = {
  url: "https://www.cpf.gov.sg/member/infohub/educational-resources/how-much-cpf-savings-you-can-use-for-your-home-purchase",
  snapshot: "policy/sources/www-cpf-gov-sg-member-infohub-educational-resources-how-much-cpf-savings-you-can-use-for-your-home-purchase.md",
  fetched: "2026-10-05",
};

const CPF_INTEREST: Source = {
  url: "https://www.cpf.gov.sg/member/growing-your-savings/earning-higher-returns/earning-attractive-interest",
  snapshot: "policy/sources/www-cpf-gov-sg-member-growing-your-savings-earning-higher-returns-earning-attractive-interest.md",
  fetched: "2026-10-05",
};
const CPF_REFUND: Source = {
  url: "https://www.cpf.gov.sg/member/infohub/educational-resources/sales-proceeds-after-selling-your-home",
  snapshot: "policy/sources/www-cpf-gov-sg-member-infohub-educational-resources-sales-proceeds-after-selling-your-home.md",
  fetched: "2026-10-05",
};
const CPF_EXPENSES: Source = {
  url: "https://www.cpf.gov.sg/member/infohub/educational-resources/hdb-option-fee-and-housing-expenses-you-should-know",
  snapshot: "policy/sources/www-cpf-gov-sg-member-infohub-educational-resources-hdb-option-fee-and-housing-expenses-you-should-know.md",
  fetched: "2026-10-05",
};
const HDB_RATE: Source = {
  url: "https://www.hdb.gov.sg/managing-my-home/finances/loan-matters/interest-rate",
  snapshot: "policy/sources/www-hdb-gov-sg-managing-my-home-finances-loan-matters-interest-rate.md",
  fetched: "2026-10-05",
};
const HDB_LOAN: Source = {
  url: "https://www.hdb.gov.sg/buying-a-flat/flat-grant-and-loan-eligibility/housing-loan/housing-loan-from-hdb",
  snapshot: "policy/sources/www-hdb-gov-sg-buying-a-flat-flat-grant-and-loan-eligibility-housing-loan-housing-loan-from-hdb.md",
  fetched: "2026-10-05",
};
const IRAS_SPOUSES: Source = {
  url: "https://www.iras.gov.sg/taxes/stamp-duty/for-property/appeals-refunds-reliefs-and-remissions/common-stamp-duty-remissions-and-reliefs-for-property/remission-of-absd-for-a-married-couple",
  snapshot: "policy/sources/www-iras-gov-sg-taxes-stamp-duty-for-property-appeals-refunds-reliefs-and-remissions-common-stamp-duty-remissions-and-re.md",
  fetched: "2026-10-05",
};

// ---- Stamp duties ---------------------------------------------------------

/** Marginal BSD bands for residential property: [band width, rate]. */
export const BSD: Versioned<[number, number][]>[] = [
  {
    effectiveFrom: "2023-02-15",
    value: [[180_000, 0.01], [180_000, 0.02], [640_000, 0.03], [500_000, 0.04], [1_500_000, 0.05], [Infinity, 0.06]],
    source: IRAS_BSD,
  },
];

/** ABSD by residency; index 0 = 1st property, 1 = 2nd, 2 = 3rd and later. */
export const ABSD: Versioned<Record<Residency, [number, number, number]>>[] = [
  { effectiveFrom: "2021-12-16", value: { SC: [0, 0.17, 0.25], PR: [0.05, 0.25, 0.3] }, source: IRAS_ABSD },
  { effectiveFrom: "2023-04-27", value: { SC: [0, 0.2, 0.3], PR: [0.05, 0.3, 0.35] }, source: IRAS_ABSD },
];

/**
 * SSD rate by full years held (index 0 = held up to 1 year). Past the end of
 * the array, no SSD. Keyed on the PURCHASE date of the property being sold.
 */
export const SSD: Versioned<number[]>[] = [
  { effectiveFrom: "2011-01-14", value: [0.16, 0.12, 0.08, 0.04], source: IRAS_SSD },
  { effectiveFrom: "2017-03-11", value: [0.12, 0.08, 0.04], source: IRAS_SSD },
  { effectiveFrom: "2025-07-04", value: [0.16, 0.12, 0.08, 0.04], source: IRAS_SSD },
];

// ---- Loans ------------------------------------------------------------------

export interface LoanRules {
  tdsr: number;
  msr: number; // HDB flats and ECs bought from developers
  bankStressRate: number; // medium-term interest rate floor, FI loans
  hdbStressRate: number; // HDB loan eligibility interest floor
  bankLtvFirst: number; // no outstanding housing loan
  bankLtvSecond: number; // one outstanding housing loan
  hdbLoanLtv: number;
  // Lower LTV tier (55% / 25%) applies if tenure exceeds these or the loan runs
  // past this age (MAS loan tenure & LTV explainer).
  ltvTierTenureHdb: number;
  ltvTierTenureOther: number;
  ltvTierAge: number;
  minCashDownFirst: number; // min cash downpayment at the 75% LTV tier
  minCashDownSecond: number; // with one outstanding housing loan
  bankLtvFirstReduced: number; // lower tier (long tenure / past age 65), no outstanding loan
  bankLtvSecondReduced: number; // lower tier, one outstanding loan
  minCashDownFirstReduced: number; // min cash at the 55% tier
  maxTenureHdb: number; // MAS max loan tenure, HDB flats
  maxTenureOther: number; // MAS max loan tenure, non-HDB
}

// Bank LTV tiers, per the MAS explainer (MAS_LTV, fetched 2026-10-05).
const BANK_LTV = {
  bankLtvFirst: 0.75,
  bankLtvSecond: 0.45,
  ltvTierTenureHdb: 25,
  ltvTierTenureOther: 30,
  ltvTierAge: 65,
  minCashDownFirst: 0.05,
  minCashDownSecond: 0.25,
  bankLtvFirstReduced: 0.55,
  bankLtvSecondReduced: 0.25,
  minCashDownFirstReduced: 0.1,
  maxTenureHdb: 30,
  maxTenureOther: 35,
};

export const LOANS: Versioned<LoanRules>[] = [
  {
    effectiveFrom: "2022-09-30",
    value: { ...BANK_LTV, tdsr: 0.55, msr: 0.3, bankStressRate: 0.04, hdbStressRate: 0.03, hdbLoanLtv: 0.8 },
    source: MAS_2022,
    note: "Stress floors 4%/3% and HDB LTV 80% from MAS_2022. TDSR/MSR caps: MAS_MSR_TDSR. Bank LTV tiers: MAS_LTV.",
    unverified: ["bank LTV tiers effective date (MAS_LTV states current limits, not when they began)"],
  },
  {
    effectiveFrom: "2024-08-20",
    value: { ...BANK_LTV, tdsr: 0.55, msr: 0.3, bankStressRate: 0.04, hdbStressRate: 0.03, hdbLoanLtv: 0.75 },
    source: MND_2024,
    note: "HDB loan LTV 80% -> 75% (MND_2024). Other values carried from MAS_2022 / MAS_LTV.",
  },
];
export const LOAN_SOURCES = { MAS_2022, MAS_MSR_TDSR, MAS_LTV, MND_2024 };

// ---- CPF ----------------------------------------------------------------------

export interface CpfLeaseRules {
  coverToAge: number; // full CPF usage if the lease covers the youngest buyer to this age
  minLeaseYears: number; // below this remaining lease, no CPF may be used
}
export const CPF_LEASE_RULES: Versioned<CpfLeaseRules>[] = [
  {
    effectiveFrom: "2019-05-10",
    value: { coverToAge: 95, minLeaseYears: 20 },
    source: CPF_USAGE,
    note: "Source states: cover the youngest buyer to 95, else OA usable up to a percentage of the lower of price and valuation.",
    unverified: ["effectiveFrom", "minLeaseYears"],
  },
];

// ---- CPF housing usage and refunds ---------------------------------------------

export interface CpfHousingRules {
  oaRate: number; // OA interest rate (accrued interest on CPF used is charged at this)
  dutiesAndLegalFromCpf: boolean; // OA may pay BSD and legal fees in full
  absdFromCpf: boolean; // ABSD from OA — not stated explicitly; treated as cash (conservative)
  refundCappedAtProceeds: boolean; // market-value sale: no cash top-up of a refund shortfall
}
export const CPF_HOUSING: Versioned<CpfHousingRules>[] = [
  {
    effectiveFrom: "2026-10-01",
    value: { oaRate: 0.025, dutiesAndLegalFromCpf: true, absdFromCpf: false, refundCappedAtProceeds: true },
    source: CPF_INTEREST,
    note: "OA rate 2.5% (1 Oct – 31 Dec 2026; legislated minimum 2.5%): CPF_INTEREST. Refund = principal + accrued interest, capped at proceeds for a market-value sale: CPF_REFUND. OA for stamp duty and legal fees: CPF_EXPENSES.",
    unverified: ["absdFromCpf (CPF_EXPENSES says 'stamp duty' without naming ABSD; modelled as cash)"],
  },
];
export const CPF_SOURCES = { CPF_INTEREST, CPF_REFUND, CPF_EXPENSES };

// HDB concessionary loan terms (beyond LTV 75% and the 3% floor in LOANS),
// per HDB's "Housing Loan from HDB" page (HDB_LOAN).
export interface HdbLoanTerms {
  maxTenure: number; // repayment period: shortest of this,
  repayByAge: number; // this minus the applicants' average age,
  leaseBuffer: number; // and remaining lease minus this
  msr: number; // monthly instalment cap, share of income
  incomeCeilingFamily: number; // average gross monthly household income
  incomeCeilingExtended: number;
  requiresCitizen: boolean; // at least one applicant is an SC
}
export const HDB_LOAN_TERMS: Versioned<HdbLoanTerms>[] = [
  {
    effectiveFrom: "2024-08-20",
    value: { maxTenure: 25, repayByAge: 65, leaseBuffer: 20, msr: 0.3, incomeCeilingFamily: 16_000, incomeCeilingExtended: 24_000, requiresCitizen: true },
    source: HDB_LOAN,
    unverified: ["effectiveFrom (snapshot states current terms; income ceilings were raised in 2026 — earlier dates may have had lower ceilings)"],
  },
];

// HDB concessionary loan rate: 0.10% above the CPF OA rate (HDB_RATE).
export const HDB_LOAN_RATE: Versioned<number>[] = [
  { effectiveFrom: "2026-04-01", value: 0.026, source: HDB_RATE, note: "2.60% p.a. for 1 Apr – 30 Sep 2026; pegged at OA + 0.10%." },
];

// ABSD remission for married couples (Stamp Duties (Spouses) (Remission of
// ABSD) Rules, per IRAS_SPOUSES): the couple must include an SC spouse and buy
// in both names only. (A) Neither owns a home → full remission on the first.
// (B) Second home → ABSD paid upfront, refunded if the first is sold within 6
// months of buying a completed second property.
export const SPOUSE_REMISSION: Versioned<{ refundWindowMonths: number }>[] = [
  { effectiveFrom: "2013-04-10", value: { refundWindowMonths: 6 }, source: IRAS_SPOUSES, unverified: ["effectiveFrom (rules dated 10 Apr 2013 per the cited legislation link)"] },
];

// ---- Lookups ------------------------------------------------------------------

export const today = (): string => new Date().toISOString().slice(0, 10);

/** The version in force on `date` (ISO). Throws if the date predates them all. */
export function at<T>(versions: Versioned<T>[], date: string = today()): Versioned<T> {
  let hit: Versioned<T> | undefined;
  for (const v of versions) if (v.effectiveFrom <= date && (!hit || v.effectiveFrom > hit.effectiveFrom)) hit = v;
  if (!hit) throw new Error(`no policy version in force on ${date}`);
  return hit;
}

export function bsd(price: number, date?: string): number {
  let remaining = price;
  let duty = 0;
  for (const [band, rate] of at(BSD, date).value) {
    if (remaining <= 0) break;
    const taxed = Math.min(remaining, band);
    duty += taxed * rate;
    remaining -= taxed;
  }
  return duty;
}

/** ABSD rate for a buyer's `nth` residential property (1 = first). */
export function absdRate(residency: Residency, nth: number, date?: string): number {
  const rates = at(ABSD, date).value[residency];
  return rates[Math.min(Math.max(1, Math.floor(nth)), 3) - 1];
}

/**
 * SSD rate for a property bought on `purchaseDate` and sold after `yearsHeld`.
 * IRAS tiers are "up to 1 year", "more than 1 and up to 2", ...; a holding of
 * exactly N years falls in the Nth tier.
 */
export function ssdRate(purchaseDate: string, yearsHeld: number): number {
  const rates = at(SSD, purchaseDate).value;
  const tier = Math.max(0, Math.ceil(yearsHeld) - 1);
  return yearsHeld < 0 ? rates[0] : rates[tier] ?? 0;
}

/** Years of holding after which no SSD applies, for a given purchase date. */
export function ssdHoldingYears(purchaseDate: string): number {
  return at(SSD, purchaseDate).value.length;
}

/** ISO date `years` before `date` (default today), for "held N years" inputs. */
export function yearsBefore(years: number, date: string = today()): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - Math.round(years * 365.25));
  return d.toISOString().slice(0, 10);
}
